import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import {
  groupDocsByType,
  buildExportZipName,
  EXPORTABLE_DOC_TYPES,
  computeSidemark,
  pickWindowTreatmentScheduleDisplay,
  formatFabricQuantity,
  estimatedShipDate,
  formatOrderStatus,
  buildWindowTreatmentInstallRows,
  // Schedules rebuild Phase 2 — template-driven side-by-side path
  buildSchedulePayloadPure,
  buildSchedulePublishFilename,
  buildScheduleStoragePath,
  nextSchedulePublishSeq,
  resolveScheduleTemplateFor,
  computeInstallSelectionIds,
  finishedPeerForAssembly,
  statusColor,
  SCHEDULE_FIELDS,
  buildScheduleItemPatch,
  scheduleOverrideDrift,
  scheduleRowBagsForTemplate,
  activeProjects,
  // Install-event filter (2026-08-24) — per-run transient event scoping
  sortInstallEvents,
  installRangeDisplay,
  itemInstallEventId,
  INSTALL_EVENT_UNSCHEDULED,
} from '../utils';
import { useAppContext } from './AppContext';
import ConfirmationModal from './ConfirmationModal';
import ScheduleGrid from './ScheduleGrid';
import {
  INK_1, INK_2, INK_3, INK_4, LINE_ROW, LINE_CONTAINER, GROUND_OFF,
  ZONE_FUCHSIA, ZONE_GREEN, ALERT_RED_TEXT, ALERT_AMBER_TEXT,
} from '../lib/designTokens';

const h = React.createElement;


// `audiences` (when present) restricts an entry to the listed audiences.
// Entries without `audiences` show for every audience. Internal / workroom /
// install have their own column shapes that don't match the standalone
// Equipment use case, so Equipment is gated to client + gc only.
//
// Equipment is already a recognized items.schedule value — it's bundled into
// FF&E for the legacy filter — but the client and contractor views sometimes
// need to render just equipment without furniture. The standalone Equipment
// entry routes through the same exact-match predicate every non-special key
// uses.
export const SCHEDULE_TYPES = [
  { key:'Lighting',            label:'Lighting Schedule'         },
  { key:'FF&E',                label:'FF&E Schedule'             },
  { key:'Window Treatments',   label:'Window Treatment Schedule' },
  { key:'Finish Schedule',     label:'Finish Schedule'           },
  { key:'Plumbing / Fixtures', label:'Plumbing Schedule'         },
  { key:'Tile & Stone',        label:'Tile & Stone Schedule'     },
  { key:'Hardware',            label:'Hardware Schedule'         },
  { key:'Art & Objects',       label:'Art & Objects Schedule'    },
  { key:'Millwork',            label:'Millwork Schedule'         },
  // Softgoods (2026-09-08) — bedding, towels, linens. Prints on its own
  // schedule through the exact-match predicate (items.schedule ===
  // 'Softgoods'); no filter change was needed. Carries NO dedicated
  // template: resolveScheduleTemplateFor falls back to the audience's
  // blank-type "Standard" layout — the same layout Furniture prints
  // through, and Softgoods needs the same core fields (size, finish,
  // notes), so there is nothing bespoke to lay out.
  //
  // Audience gate: client / internal / insurance ride the blank-type
  // "Standard" seed with no template of their own. INSTALL needs a real
  // template (Cimon 2026-09-08) — its seed carries scheduleType 'ALL', not
  // blank, so the generic branch skips it and resolution would return null;
  // install is also the one audience whose SELECTION comes off the template
  // (computeInstallSelectionIds reads rowFilter), so it cannot borrow a
  // layout. Supplied by 20260908_softgoods_install_template.sql, which
  // clones the live 'Install / Delivery' row.
  //
  // gc + workroom stay OFF: their templates are all type-specific, so they
  // would resolve to NO template and throw. Add one first if softgoods ever
  // need a gc/workroom issue.
  { key:'Softgoods',           label:'Softgoods Schedule', audiences: ['client', 'internal', 'install', 'insurance'] },
  { key:'Equipment',           label:'Equipment Schedule', audiences: ['client', 'gc'] },
  { key:'Appliances',          label:'Appliance Schedule', audiences: ['client', 'gc'] },
  { key:'ALL',                 label:'ALL'                       },
];

// Returns the visible schedule types for a given audience. Entries with an
// `audiences` allow-list are hidden from audiences not on the list.
export function getScheduleTypesForAudience(audience) {
  return SCHEDULE_TYPES.filter(function(s) {
    return !s.audiences || s.audiences.indexOf(audience) >= 0;
  });
}

const AUDIENCES = [
  { key:'client',   label:'Client'                },
  { key:'gc',       label:'Contractor / GC'       },
  { key:'internal', label:'Internal / Procurement'},
  { key:'workroom', label:'Workroom'               },
  { key:'install',  label:'Install / Delivery'    },
  // Insurance (2026-07-13): outside audience for the owner's-rep /
  // insurer valuation list — client tax-inclusive prices, never net.
  // Use the NEW engine (Generate (new) / grid) with the 'Insurance —
  // Standard' template; the legacy renderer has no insurance branch.
  { key:'insurance', label:'Insurance'             },
];

// ── Design-handoff primitives (design_handoff_schedules_page, 2026-07-07) ──
// High-fidelity port: values below are final per the handoff README.
const CHEVRON_BG = "url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%228%22 height=%2210%22><path d=%22M1 3.5 4 .8 7 3.5M1 6.5 4 9.2 7 6.5%22 stroke=%22%23888880%22 fill=%22none%22/></svg>') no-repeat right 10px center";
const EYEBROW_STYLE = { fontSize: 10, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#888880', fontWeight: 500 };
const SELECT_STYLE = {
  appearance: 'none', WebkitAppearance: 'none',
  background: '#FFFFFF ' + CHEVRON_BG,
  border: '1px solid ' + LINE_CONTAINER, borderRadius: 6,
  padding: '7px 28px 7px 12px', fontSize: 13, color: INK_1, fontFamily: 'inherit',
};
const LINK_BTN_STYLE = {
  background: 'none', border: 'none', padding: 0, fontFamily: 'inherit',
  fontSize: 12, color: INK_2, textDecoration: 'underline', textUnderlineOffset: 2, cursor: 'pointer',
};
const GHOST_BTN_STYLE = {
  fontFamily: 'inherit', fontSize: 12, fontWeight: 500, padding: '7px 18px',
  borderRadius: 6, border: '1px solid ' + LINE_CONTAINER, background: '#fff', color: INK_1, cursor: 'pointer',
};
const PRIMARY_BTN_STYLE = {
  fontFamily: 'inherit', fontSize: 12, fontWeight: 500, padding: '7px 18px',
  borderRadius: 6, border: '1px solid #111111', background: '#111111', color: '#fff', cursor: 'pointer',
};
// Display labels for schedule-type keys (keys stay canonical).
const TYPE_DISPLAY = { 'Finish Schedule': 'Finish', 'Plumbing / Fixtures': 'Plumbing' };
// Selection cue variant — owner is testing 'ink' vs 'fuchsia-dot' (handoff §5).
const SELECTION_CUE = 'ink';

function DSel({ label, value, onChange, minWidth, children }) {
  return h('div', { style: { display: 'flex', flexDirection: 'column', gap: 6 } },
    h('div', { style: EYEBROW_STYLE }, label),
    h('select', {
      value, onChange: e => onChange(e.target.value),
      style: Object.assign({}, SELECT_STYLE, { minWidth: minWidth || 170 }),
    }, children)
  );
}

// 14px ink checkbox glyph box (handoff item/room checkbox).
function CheckGlyph({ state }) { // 'on' | 'partial' | 'off'
  const on = state === 'on';
  return h('span', {
    style: {
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      width: 14, height: 14, borderRadius: 3, flexShrink: 0,
      border: '1px solid ' + (on ? '#111111' : LINE_CONTAINER),
      background: on ? '#111111' : '#FFFFFF',
      color: state === 'partial' ? INK_2 : '#FFFFFF',
      fontSize: 10, lineHeight: 1,
    },
  }, on ? '\u2713' : state === 'partial' ? '\u2013' : '');
}

// ── Install-event filter (2026-08-24) ────────────────────────────────────────
// Multi-select checkbox popover styled to sit beside the DSel pulldowns —
// scopes the install run to one or more named install events. Per-run
// TRANSIENT (like the receiver filter): nothing persists on the schedules
// row; the published PDF's header + frozen payload record the scope.
// 'All Events' = no filter (today's room-banded output); any explicit
// selection filters the run AND flips the page to event bands.
// 'Unscheduled' = items with no event assignment (manual dates included) —
// without it, unassigned items would silently vanish from install-day runs.
function InstallEventSelect({ events, value, onChange }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    function close() { setOpen(false); }
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [open]);

  const selected = value || [];
  function toggle(id) {
    onChange(selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id]);
  }
  function summary() {
    if (selected.length === 0) return 'All Events';
    if (selected.length === 1) {
      if (selected[0] === INSTALL_EVENT_UNSCHEDULED) return 'Unscheduled';
      const ev = events.find(e => String(e.id) === selected[0]);
      return (ev && ev.label) || '1 Event';
    }
    return selected.length + ' Events';
  }
  const rowStyle = {
    display: 'flex', alignItems: 'center', gap: 8, width: '100%',
    padding: '7px 10px', background: 'none', border: 'none', cursor: 'pointer',
    fontFamily: 'inherit', fontSize: 12, color: INK_1, textAlign: 'left',
  };
  return h('div', { style: { display: 'flex', flexDirection: 'column', gap: 6, position: 'relative' }, onClick: e => e.stopPropagation() },
    h('div', { style: EYEBROW_STYLE }, 'Install Event'),
    h('button', {
      onClick: () => setOpen(o => !o),
      style: Object.assign({}, SELECT_STYLE, { minWidth: 170, cursor: 'pointer', textAlign: 'left' }),
    }, summary()),
    open && h('div', {
      style: { position: 'absolute', left: 0, top: '100%', marginTop: 4, zIndex: 50, background: '#fff', border: '1px solid ' + LINE_CONTAINER, borderRadius: 6, boxShadow: '0 8px 32px rgba(0,0,0,0.12)', minWidth: 240, padding: 4 },
    },
      h('button', { key: 'all', onClick: () => onChange([]), style: rowStyle },
        h(CheckGlyph, { state: selected.length === 0 ? 'on' : 'off' }),
        h('span', null, 'All Events')
      ),
      events.map(ev => h('button', { key: ev.id, onClick: () => toggle(String(ev.id)), style: rowStyle },
        h(CheckGlyph, { state: selected.includes(String(ev.id)) ? 'on' : 'off' }),
        h('span', null, ev.label || 'Install event'),
        ev.startDate && h('span', { style: { color: INK_3, marginLeft: 'auto', paddingLeft: 12, whiteSpace: 'nowrap' } }, installRangeDisplay(ev.startDate, ev.days))
      )),
      h('button', { key: 'unsched', onClick: () => toggle(INSTALL_EVENT_UNSCHEDULED), style: Object.assign({}, rowStyle, { borderTop: '1px solid ' + LINE_ROW }) },
        h(CheckGlyph, { state: selected.includes(INSTALL_EVENT_UNSCHEDULED) ? 'on' : 'off' }),
        h('span', null, 'Unscheduled')
      )
    )
  );
}

function HoverRow(props) {
  // Grid row with the handoff's #FAFAF8 hover (inline styles can't :hover).
  const [hov, setHov] = useState(false);
  return h('div', Object.assign({}, props.attrs || {}, {
    onMouseEnter: () => setHov(true),
    onMouseLeave: () => setHov(false),
    style: Object.assign({}, props.style, hov ? { background: GROUND_OFF } : {}),
  }), props.children);
}

function isParent(it) {
  return it.parentId === null || it.parentId === undefined || it.parentId === '';
}

// ── ITEM SELECTOR — handoff anatomy (design_handoff_schedules_page §5) ────────
// One global eyebrow header row; clickable room bands (toggle whole room)
// with ✓/– checkboxes and a partial-selection whisper; item rows on the
// shared grid with ink-shift selection cue (SELECTION_CUE 'ink'; the
// fuchsia-dot variant is coded behind the flag — owner testing both).
const ITEMS_GRID = '34px 52px minmax(240px, 1.6fr) 70px 1fr 1.1fr 1.4fr 150px';

function ItemSelector({items,scheduleType,projectLabel,selectedIds,onToggle,onToggleAll,onSetAll}) {
  const filtered = items.filter(it => {
    if (!isParent(it)) return false;
    if (projectLabel && it.project !== projectLabel) return false;
    if (scheduleType === 'ALL') return true;
    if (scheduleType === 'FF&E') return it.schedule === 'Furniture' || it.schedule === 'Equipment';
    if (scheduleType === 'Finish Schedule') return it.schedule === 'Finish Schedule' || it.schedule === 'Tile & Stone';
    return it.schedule === scheduleType;
  });

  if (!projectLabel) return h('div',{style:{padding:'40px',textAlign:'center',color:INK_3,border:'1px dashed '+LINE_CONTAINER,borderRadius:6,fontSize:13}},'Select a project to see items');
  if (filtered.length === 0) return h('div',{style:{padding:'32px',textAlign:'center',color:INK_3,border:'1px dashed '+LINE_CONTAINER,borderRadius:6,fontSize:13}},'No '+(TYPE_DISPLAY[scheduleType]||scheduleType)+' items in this project yet');

  const byRoom = {};
  const roomNumMap = {};
  filtered.forEach(it => {
    const name = it.roomName || 'No Room';
    const num  = it.roomNumber || '';
    const room = num ? (num + '\x1F' + name) : name;
    if (!byRoom[room]) byRoom[room]=[];
    byRoom[room].push(it);
    if (!roomNumMap[room] && num) roomNumMap[room] = num;
  });
  Object.values(byRoom).forEach(arr=>arr.sort((a,b)=>(a.tag||'').localeCompare(b.tag||'',undefined,{numeric:true,sensitivity:'base'})));
  const sortedRoomEntries = Object.entries(byRoom).sort(([a],[b])=>{
    const na=parseFloat(roomNumMap[a])||9999, nb=parseFloat(roomNumMap[b])||9999;
    return na!==nb ? na-nb : a.localeCompare(b);
  }).map(([key,roomItems])=>{
    const sep=key.indexOf('\x1F');
    return { key, roomName: sep>-1?key.slice(sep+1):key, roomNum: roomNumMap[key]||'', items: roomItems };
  });

  const selectedCount = filtered.filter(it=>selectedIds.includes(it.id)).length;
  const eyeCell = (txt) => h('div',{style:EYEBROW_STYLE},txt);
  const bodyCell = (txt) => h('span',{style:{fontSize:13,color:INK_2,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}},txt||'\u2014');

  return h('div',null,
    // Selection summary + global link actions
    h('div',{style:{display:'flex',alignItems:'center',gap:14,marginBottom:12}},
      h('div',{style:{fontSize:13,color:INK_2}},
        h('span',{style:{fontWeight:500,color:INK_1}},selectedCount),' of ',filtered.length,' items selected'),
      h('button',{onClick:()=>onSetAll(filtered,true),style:LINK_BTN_STYLE},'Select all'),
      h('button',{onClick:()=>onSetAll(filtered,false),style:LINK_BTN_STYLE},'Clear')
    ),
    // One global header row
    h('div',{style:{display:'grid',gridTemplateColumns:ITEMS_GRID,gap:'0 14px',alignItems:'center',padding:'7px 12px',borderBottom:'1px solid '+LINE_CONTAINER}},
      h('div'),eyeCell('Photo'),eyeCell('Item / Product'),eyeCell('Qty'),eyeCell('Manufacturer'),eyeCell('Model'),eyeCell('Finish'),eyeCell('Status')
    ),
    sortedRoomEntries.map(({key,roomName,roomNum,items:roomItems})=>{
      const selN = roomItems.filter(it=>selectedIds.includes(it.id)).length;
      const all = selN === roomItems.length;
      return h('div',{key},
        // Room band — clickable, toggles the whole room
        h('div',{
          onClick:()=>onToggleAll(roomItems),
          style:{display:'flex',alignItems:'center',gap:10,background:GROUND_OFF,padding:'8px 12px',borderBottom:'1px solid '+LINE_ROW,cursor:'pointer'},
        },
          h(CheckGlyph,{state: all?'on':(selN>0?'partial':'off')}),
          h('span',{style:Object.assign({},EYEBROW_STYLE,{color:INK_2})},
            'Location: '+(roomNum?roomNum+' \u2014 ':'')+roomName.toUpperCase()+' ('+roomItems.length+')'),
          !all && selN>0 && h('span',{style:{fontSize:11,color:INK_4}},selN+' of '+roomItems.length+' selected')
        ),
        roomItems.map(it=>{
          const heroImg=it.imageUrl||(it.images&&it.images.length>0?it.images[it.heroIndex||0]?.url:null);
          const checked=selectedIds.includes(it.id);
          const displayName=it.productName||it.item||'\u2014';
          const subLine=(it.productName&&it.item&&it.item!==it.productName)?it.item:'';
          return h(HoverRow,{
            key:it.id,
            attrs:{onClick:()=>onToggle(it.id)},
            style:{display:'grid',gridTemplateColumns:ITEMS_GRID,gap:'0 14px',alignItems:'center',padding:'9px 12px',borderBottom:'1px solid '+LINE_ROW,cursor:'pointer'},
          },
            h(CheckGlyph,{state:checked?'on':'off'}),
            heroImg
              ? h('img',{src:heroImg,style:{width:36,height:36,objectFit:'cover',border:'1px solid '+LINE_CONTAINER,borderRadius:4,display:'block'}})
              : h('span',{style:{display:'block',width:36,height:36,border:'1px solid '+LINE_CONTAINER,borderRadius:4,background:'#fff'}}),
            h('span',{style:{minWidth:0}},
              h('span',{style:{display:'flex',alignItems:'center'}},
                SELECTION_CUE==='fuchsia-dot' && h('span',{style:{display:checked?'inline-block':'none',width:6,height:6,borderRadius:'50%',background:ZONE_FUCHSIA,marginRight:8,flexShrink:0}}),
                h('span',{style:{fontSize:13,fontWeight:500,color:checked?INK_1:INK_3,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}},displayName),
                it.procurementClass==='owner_furnished'&&h('span',{'data-testid':'procurement-class-badge','data-class':'owner_furnished',title:'Owner Furnished',style:{fontSize:9,fontWeight:500,padding:'1px 5px',borderRadius:3,border:'0.5px solid '+LINE_CONTAINER,background:'#fff',color:'#888880',whiteSpace:'nowrap',marginLeft:8,flexShrink:0}},'OF'),
                it.procurementClass==='by_others'&&h('span',{'data-testid':'procurement-class-badge','data-class':'by_others',title:'By Others',style:{fontSize:9,fontWeight:500,padding:'1px 5px',borderRadius:3,border:'0.5px solid '+LINE_CONTAINER,background:'#fff',color:'#888880',whiteSpace:'nowrap',marginLeft:8,flexShrink:0}},'BO')
              ),
              subLine && h('span',{style:{display:'block',fontSize:10,letterSpacing:'0.07em',textTransform:'uppercase',color:INK_4,marginTop:2}},subLine)
            ),
            bodyCell((it.qty||'1')+' '+String(it.qtyType||'ea').toLowerCase()),
            bodyCell(it.specs?.manufacturer||it.manufacturer||it.vendor),
            bodyCell(it.model||it.specs?.model),
            bodyCell(it.finish||it.specs?.finish),
            h('span',{style:{display:'flex',alignItems:'center',gap:7}},
              h('span',{style:{width:8,height:8,borderRadius:'50%',background:statusColor(it.status),flexShrink:0}}),
              h('span',{style:{fontSize:12,color:INK_3}},it.status||'\u2014')
            )
          );
        })
      );
    })
  );
}

// ── NEW ENGINE — template-driven side-by-side path (Phase 2) ─────────────────
// docs/SCHEDULES_REBUILD_SPEC.md §5. Renders through
// utils.buildSchedulePayloadPure → /api/generate-schedule (Gotham, server
// side) and publishes immutable dated versions via the document_sends
// pattern with the {YYYYMMDD}.{projectCode}.{Type}.{Audience}.pdf naming.
// Legacy jsPDF path fully deleted 2026-07-18 (Cimon's Phase 5
// sign-off): renderers, Save Version/document_versions dead code,
// the embedded logo, and the jspdf import are gone — this panel's
// Generate/Publish (api/generate-schedule.py) is the only path.

function NewEngineSchedulePanel({ projectLabel, scheduleType, audience, selectedIds, dateLabel, selectedProject, items, assemblies, receiverFilter, installExtraIds, installExcludeIds, installEventFilter, legacySlot }) {
  const ctx = useAppContext();
  const [working, setWorking] = useState('');        // '' | 'preview' | 'publish'
  const [statusMsg, setStatusMsg] = useState(null);  // { ok, text }
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Phase 3 — grid & template editor state.
  const [tplId, setTplId] = useState('');            // explicit template pick ('' = auto)
  const [tplSaveState, setTplSaveState] = useState(''); // '' | 'saving' | 'saved' | 'error' (action-row whisper)
  const [gridViolations, setGridViolations] = useState([]); // audience-gate violations reported by the grid

  // Publishing is open to EVERYONE (Cimon 2026-07-20: "this needs to be
  // completely open"). Consistent with the schedules rebuild's locked
  // decision — the safety boundary is the AUDIENCE gate (internal-only
  // fields are hard-refused on client/gc/insurance templates for every
  // user), not a role gate. Schedules are operational documents the
  // whole team produces; the prior owner/admin canPublish gate was a
  // Phase 2 plumbing default, not a decision.

  // Every template usable for this view: exact type match first, then the
  // audience's generic ("Standard") templates. The picker makes the active
  // template explicit once copies exist.
  const templatesForView = useMemo(() => {
    const list = (ctx.scheduleTemplates || []).filter(t =>
      t.audience === audience && (t.scheduleType === scheduleType || !t.scheduleType));
    return list.slice().sort((a, b) => {
      const ax = a.scheduleType === scheduleType ? 0 : 1;
      const bx = b.scheduleType === scheduleType ? 0 : 1;
      return ax !== bx ? ax - bx : String(a.name).localeCompare(String(b.name));
    });
  }, [ctx.scheduleTemplates, scheduleType, audience]);

  useEffect(() => { setTplId(''); }, [scheduleType, audience]);

  const template = useMemo(() => {
    if (tplId) {
      const picked = templatesForView.find(t => t.id === tplId);
      if (picked) return picked;
    }
    return resolveScheduleTemplateFor(ctx.scheduleTemplates, scheduleType, audience);
  }, [tplId, templatesForView, ctx.scheduleTemplates, scheduleType, audience]);

  // Templates are a live single source (Cimon, 2026-07-07): grid structure
  // edits autosave — no copy/overwrite ceremony. The deliberate fork
  // ("New template from this layout…") is the only way a second template
  // is born; it's project-agnostic, so a Window Treatment Workroom
  // template built on Hoback serves every future project.
  async function handleAutosaveTemplate(tpl) {
    setTplSaveState('saving');
    const saved = await ctx.saveScheduleTemplate(tpl);
    setTplSaveState(saved ? 'saved' : 'error');
  }

  async function handleForkTemplate(name, tpl) {
    setTplSaveState('saving');
    const copy = Object.assign({}, tpl, { name: name, isSeed: false });
    delete copy.id;
    delete copy.createdAt;
    delete copy.updatedAt;
    const saved = await ctx.saveScheduleTemplate(copy);
    setTplSaveState(saved ? 'saved' : 'error');
    if (saved) {
      setTplId(saved.id); // the new template becomes active for this view
      setStatusMsg({ ok: true, text: 'Created template “' + name + '” — now active for this view (usable on any project).' });
    } else {
      setStatusMsg({ ok: false, text: 'Could not create the template.' });
    }
  }

  // Phase 4 — cell editing (spec §4.4). Three write paths:
  //   item          → updateItem write-through (audited via change_history);
  //                   dotted specs.* targets merge into the jsonb.
  //   override      → schedules.cell_overrides (this schedule only; marked
  //                   with the fuchsia dot; drift-warned at publish).
  //   clearOverride → removes the override; the item value shows again.
  //   custom        → schedules.custom_values (never touches the item).
  // Schedule-side edits find-or-create the schedules row (same row the
  // publish flow anchors send history to) and save immediately —
  // the schedule is a living document, saved as you go.
  function persistScheduleData(mutate) {
    const base = existingSchedule ? Object.assign({}, existingSchedule) : buildDoc();
    base.itemIds = effectiveItemIds; // selection rides along, stays current
    mutate(base);
    return ctx.saveSchedule(base);
  }

  function handleCellCommit(payload) {
    if (!payload) return;
    if (payload.kind === 'item') {
      const meta = SCHEDULE_FIELDS[payload.field];
      const item = (items || []).find(it => it.id === payload.itemId);
      if (!meta || !meta.itemField || !item) return;
      const patch = buildScheduleItemPatch(item, meta.itemField, payload.value);
      if (patch) ctx.updateItem(payload.itemId, patch);
      return;
    }
    if (payload.kind === 'override') {
      persistScheduleData(doc => {
        const all = Object.assign({}, doc.cellOverrides || {});
        const mine = Object.assign({}, all[payload.itemId] || {});
        mine[payload.field] = payload.value;
        all[payload.itemId] = mine;
        doc.cellOverrides = all;
      });
      return;
    }
    if (payload.kind === 'clearOverride') {
      persistScheduleData(doc => {
        const all = Object.assign({}, doc.cellOverrides || {});
        const mine = Object.assign({}, all[payload.itemId] || {});
        delete mine[payload.field];
        if (Object.keys(mine).length === 0) delete all[payload.itemId];
        else all[payload.itemId] = mine;
        doc.cellOverrides = all;
      });
      return;
    }
    if (payload.kind === 'custom') {
      persistScheduleData(doc => {
        const all = Object.assign({}, doc.customValues || {});
        const mine = Object.assign({}, all[payload.itemId] || {});
        mine[payload.colId] = payload.value;
        all[payload.itemId] = mine;
        doc.customValues = all;
      });
    }
  }


  const existingSchedule = useMemo(() => (ctx.schedules || []).find(s =>
    s.projectLabel === projectLabel && s.scheduleType === scheduleType && s.audience === audience
  ) || null, [ctx.schedules, projectLabel, scheduleType, audience]);

  const sends = useMemo(() => {
    if (!existingSchedule) return [];
    return (ctx.documentSends || []).filter(s =>
      s.documentType === 'schedule' && s.documentId === existingSchedule.id);
  }, [ctx.documentSends, existingSchedule]);

  // Install audience: selection derives from the template's row filter
  // (statuses + procurement classes) plus the picker's receiver filter and
  // per-run extras/excludes — spec decision #3 (install is columns + a row
  // filter, not a separate schedule). Other audiences use the picker's
  // explicit selection as-is.
  const effectiveItemIds = useMemo(() => {
    if (audience !== 'install') return selectedIds;
    return computeInstallSelectionIds(items, {
      rowFilter: (template && template.rowFilter) || {},
      projectLabel,
      scheduleType,
      receiverFilter: receiverFilter,
      projectReceiverId: selectedProject && selectedProject.receiverId,
      vendors: ctx.vendors || [],
      extraIds: installExtraIds || [],
      excludeIds: installExcludeIds || [],
      eventFilter: installEventFilter || [],
      installEvents: (selectedProject && selectedProject.installEvents) || [],
    });
  }, [audience, selectedIds, items, template, projectLabel, scheduleType, receiverFilter, installExtraIds, installExcludeIds, installEventFilter, selectedProject]);

  const ready = !!(projectLabel && template && effectiveItemIds.length > 0);

  // Publish drift warning (spec decision #4): overrides that differ from
  // what the item currently says, computed fresh at confirm time. Sits
  // after effectiveItemIds/existingSchedule so render-time evaluation
  // never hits their temporal dead zone.
  const publishDrift = useMemo(() => {
    if (!confirmOpen || !template) return [];
    try {
      const bags = scheduleRowBagsForTemplate({
        template,
        selectedItems: (items || []).filter(it => effectiveItemIds.indexOf(it.id) >= 0),
        assemblies, scheduleType, audience,
        vendors: ctx.vendors || [],
      });
      return scheduleOverrideDrift(buildDoc(), bags);
    } catch (e) { return []; }
  }, [confirmOpen, template, items, assemblies, effectiveItemIds, scheduleType, audience, existingSchedule]);

  function buildDoc() {
    return {
      projectLabel, scheduleType, audience,
      itemIds: effectiveItemIds,
      cellOverrides: (existingSchedule && existingSchedule.cellOverrides) || {},
      customValues: (existingSchedule && existingSchedule.customValues) || {},
      title: '',
    };
  }

  async function renderPdfBytes() {
    const payload = buildSchedulePayloadPure({
      schedule: buildDoc(), template, items, assemblies,
      project: selectedProject || { label: projectLabel },
      vendors: ctx.vendors || [],
      dateLabel,
      // Event-scoped run: event bands + header scope. Transient — the
      // frozen payload on the send row is the record.
      installEventFilter: audience === 'install' ? (installEventFilter || []) : [],
    });
    const res = await fetch('/api/generate-schedule', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      let msg = 'HTTP ' + res.status;
      try { const j = await res.json(); msg = j.error || msg; } catch (e) { /* keep */ }
      throw new Error(msg);
    }
    return { bytes: await res.arrayBuffer(), payload };
  }

  async function preview() {
    setWorking('preview'); setStatusMsg(null);
    try {
      const r = await renderPdfBytes();
      window.open(URL.createObjectURL(new Blob([r.bytes], { type: 'application/pdf' })), '_blank');
    } catch (e) {
      setStatusMsg({ ok: false, text: 'Generate failed: ' + e.message });
    }
    setWorking('');
  }

  async function publish() {
    setConfirmOpen(false);
    setWorking('publish'); setStatusMsg(null);
    try {
      const projectCode = (selectedProject && selectedProject.projectCode) || '';
      if (!projectCode) throw new Error('this project has no Item Number Code (e.g. 26HOB) — set it in Projects → edit, then publish.');
      // 1. The working schedules row anchors the send history (its uuid is
      //    document_sends.document_id). Create it on first publish; refresh
      //    the selection on every publish.
      const base = existingSchedule ? Object.assign({}, existingSchedule, buildDoc()) : buildDoc();
      if (template && template.id) base.templateId = template.id;
      const sched = await ctx.saveSchedule(base);
      if (!sched) throw new Error('could not save the schedule record');
      // 2. Render through the pure builder + server renderer.
      const r = await renderPdfBytes();
      // 3. Dated immutable filename; same-day republish appends -2, -3…
      const dateISO = new Date().toISOString().slice(0, 10);
      const seq = nextSchedulePublishSeq(ctx.documentSends, sched.id, dateISO);
      const fname = buildSchedulePublishFilename({ dateISO, projectCode, scheduleType, audience, seq });
      if (!fname) throw new Error('could not build the publish filename');
      const relPath = buildScheduleStoragePath(projectLabel, fname);
      const blob = new Blob([r.bytes], { type: 'application/pdf' });
      const up = await supabase.storage.from('schedules').upload(relPath, blob, { contentType: 'application/pdf', upsert: false });
      if (up.error) throw new Error('PDF upload failed: ' + up.error.message);
      // 4. Frozen payload + stored PDF = the immutable version record.
      const rec = await ctx.recordDocumentSend({
        documentType: 'schedule', documentId: sched.id,
        payload: r.payload, storagePath: 'schedules/' + relPath,
      });
      if (!rec || !rec.ok) throw new Error((rec && rec.error) || 'send record failed — has 20260706_document_sends_schedule.sql been run?');
      // 5. Local download so it can be emailed straight away.
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob); a.download = fname; a.click();
      setStatusMsg({ ok: true, text: 'Published ' + fname + ' — saved to the issues below and downloaded.' });
    } catch (e) {
      setStatusMsg({ ok: false, text: 'Publish failed: ' + e.message });
    }
    setWorking('');
  }

  async function openSend(s) {
    const rel = String(s.storagePath || '').replace(/^schedules\//, '');
    const r = await supabase.storage.from('schedules').createSignedUrl(rel, 300);
    if (r.data && r.data.signedUrl) window.open(r.data.signedUrl, '_blank');
    else setStatusMsg({ ok: false, text: 'Could not create a download link for that version.' });
  }

  const previewName = buildSchedulePublishFilename({
    dateISO: new Date().toISOString().slice(0, 10),
    projectCode: (selectedProject && selectedProject.projectCode) || '',
    scheduleType, audience,
  });

  return h('div', { style: { border: '1px solid ' + LINE_CONTAINER, borderRadius: 8, padding: '20px 24px', background: '#fff' } },
    // The publish filename IS this document's title (Cimon 2026-07-08).
    previewName
      ? h('div', null,
          h('div', { style: EYEBROW_STYLE }, 'Publishes as'),
          h('div', { style: { fontSize: 15, fontWeight: 500, color: INK_1, marginTop: 4, letterSpacing: '0.01em' } }, previewName)
        )
      : h('div', { style: EYEBROW_STYLE }, 'Schedule'),
    !template
      ? h('div', { style: { fontSize: 11, color: INK_2 } },
          'No template found for this schedule type + audience. Run the 20260706 migrations (schedule tables + seeds) in Supabase, then reload.')
      : h('div', null,
          h('div', { style: { display: 'flex', alignItems: 'center', gap: 10, marginTop: 14, flexWrap: 'wrap' } },
            h('span', { style: EYEBROW_STYLE }, 'Template'),
            templatesForView.length > 1
              ? h('select', {
                  value: template.id || '',
                  onChange: e => setTplId(e.target.value),
                  style: Object.assign({}, SELECT_STYLE, { padding: '6px 28px 6px 12px' }),
                },
                  templatesForView.map(t => h('option', { key: t.id, value: t.id },
                    t.name + (t.isSeed ? ' \u00b7 default' : ''))))
              : h('span', { style: { fontSize: 13, color: INK_1, fontWeight: 500 } },
                  template.name + (template.isSeed ? ' \u00b7 default' : ''))
          ),
          h('div', { style: { display: 'flex', gap: 10, alignItems: 'center', marginTop: 16, flexWrap: 'wrap' } },
            h('button', { disabled: !ready || !!working, onClick: preview, style: Object.assign({}, GHOST_BTN_STYLE, (!ready || working) ? { opacity: .5, cursor: 'default' } : {}) },
              working === 'preview' ? 'Opening preview\u2026' : 'Preview PDF'),
            h('button', { disabled: !ready || !!working, onClick: () => setConfirmOpen(true), style: Object.assign({}, PRIMARY_BTN_STYLE, (!ready || working) ? { opacity: .5, cursor: 'default' } : {}) },
              working === 'publish' ? 'Publishing\u2026' : 'Publish issue\u2026'),
            statusMsg && h('span', { style: { fontSize: 12, color: statusMsg.ok ? ZONE_GREEN : ALERT_RED_TEXT } }, statusMsg.text),
            !statusMsg && !ready && projectLabel && template && h('span', { style: { fontSize: 12, color: INK_3 } },
              audience === 'install'
                ? ((installEventFilter || []).length > 0
                    ? 'No items on the selected install events'
                    : 'No install-eligible items (row filter: Order Confirmed+, owner-furnished, by-others)')
                : 'Select at least one item on the Items view'),
            !statusMsg && !projectLabel && h('span', { style: { fontSize: 12, color: INK_3 } }, 'Select a project first'),
            h('span', { style: { flex: 1 } }),
            gridViolations.length > 0
              ? h('span', { style: { fontSize: 11, color: ALERT_RED_TEXT } }, 'Not saved: ' + gridViolations[0].path + ' is internal-only on a ' + audience + ' template')
              : tplSaveState === 'saving' ? h('span', { style: { fontSize: 11, color: INK_3 } }, 'Saving\u2026')
              : tplSaveState === 'error' ? h('span', { style: { fontSize: 11, color: ALERT_RED_TEXT } }, 'Save failed \u2014 edit again to retry')
              : tplSaveState === 'saved' ? h('span', { style: { fontSize: 11, color: ZONE_GREEN } }, 'Saved \u2713')
              : null
          ),

          h('div', { style: { fontSize: 11, color: INK_3, marginTop: 8 } },
            'Preview opens the PDF so you can check it \u2014 nothing is saved. Publish creates a permanent, dated issue in the history below and downloads it for sending.'),
          // The grid IS the schedule view (handoff §6.5); its edit-status
          // (audience violations) reports up into the action-row whisper.
          h('div', { style: { marginTop: 20 } },
            projectLabel && h(ScheduleGrid, {
              key: template.id || 'tpl',
              template,
              scheduleDoc: buildDoc(),
              items, assemblies,
              project: selectedProject,
              vendors: ctx.vendors || [],
              installEventFilter: audience === 'install' ? (installEventFilter || []) : [],
              canEditTemplates: true, // everyone edits templates (spec §2 #2); the audience gate is the guardrail
              onAutosaveTemplate: handleAutosaveTemplate,
              onForkTemplate: handleForkTemplate,
              onEditStatus: st => setGridViolations((st && st.violations) || []),
              onCellCommit: handleCellCommit,
            })
          ),
          sends.length > 0 && h('div', { style: { marginTop: 32 } },
            h('div', { style: Object.assign({}, EYEBROW_STYLE, { paddingBottom: 8, borderBottom: '1px solid ' + LINE_CONTAINER }) },
              'Published issues'),
            sends.map((sd, i) =>
              h('div', { key: sd.id, style: { display: 'grid', gridTemplateColumns: '44px 1fr 110px 70px', gap: '0 16px', alignItems: 'center', padding: '9px 0', borderBottom: '1px solid ' + LINE_ROW } },
                h('span', { style: { fontSize: 12, fontWeight: 500, color: INK_1 } }, 'v' + (sends.length - i)),
                h('span', { style: { fontSize: 12, color: INK_2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, (String(sd.storagePath || '').split('/').pop()) || '(no file stored)'),
                h('span', { style: { fontSize: 12, color: INK_3 } }, sd.createdAt ? new Date(sd.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''),
                sd.storagePath
                  ? h('button', { onClick: () => openSend(sd), style: { background: 'none', border: 'none', padding: 0, fontFamily: 'inherit', fontSize: 12, color: INK_1, textDecoration: 'underline', textUnderlineOffset: 2, cursor: 'pointer', textAlign: 'left' } }, '\u2193 PDF')
                  : h('span')
              )
            )
          )
        ),
    legacySlot || null,
    h(ConfirmationModal, {
      open: confirmOpen,
      heading: 'Publish this issue?',
      body: 'This saves a permanent, dated issue of this schedule' + (previewName ? ' — ' + previewName + ' —' : '') +
        ' with a frozen copy of every value on it, adds it to Published issues, and downloads the PDF for sending. ' +
        'Later edits to items will NOT change this published issue.' +
        (publishDrift.length > 0
          ? ' ⚠ ' + publishDrift.length + ' cell' + (publishDrift.length !== 1 ? 's are' : ' is') +
            ' overridden away from the item records on this schedule (' +
            publishDrift.slice(0, 3).map(d => (SCHEDULE_FIELDS[d.field] && SCHEDULE_FIELDS[d.field].label) || d.field).join(', ') +
            (publishDrift.length > 3 ? ', …' : '') + ') — the published PDF will show the overridden values.'
          : ''),
      confirmLabel: 'Publish',
      onConfirm: publish,
      onCancel: () => setConfirmOpen(false),
      confirming: working === 'publish',
    })
  );
}

// ── MAIN ──────────────────────────────────────────────────────────────────────
export default function SchedulesTab({items,projects,vendors,firmSettings,assemblies}) {
  const [projectLabel,setProjectLabel] = useState('');
  // Page view toggle (redesign 2026-07-07): ITEMS (pick what's on the
  // schedule) vs SCHEDULE (shape, generate, publish). No more stacking.
  const [view,setView] = useState('items');
  const [scheduleType,setScheduleType] = useState('Lighting');
  const [audience,setAudience]         = useState('client');
  const [date,setDate]                 = useState(new Date().toLocaleDateString('en-US',{day:'2-digit',month:'short',year:'numeric'}).toUpperCase());
  const [selectedIds,setSelectedIds]   = useState([]);
  const [installExtraIds,setInstallExtraIds] = useState([]);
  // installExcludeIds — items the user has deselected from the
  // auto-included set on the install schedule picker. Lets the
  // bookkeeper override auto-eligibility on a per-run basis
  // without changing global filter rules. Item ids for standard
  // items; '__wt_asm__:' + asmId for WT assemblies. 2026-06-09.
  const [installExcludeIds,setInstallExcludeIds] = useState([]);
  const [exportDropOpen, setExportDropOpen] = useState(false);
  const [exporting, setExporting]           = useState(false);
  const [exportResult, setExportResult]     = useState(null);
  const [receiverFilter, setReceiverFilter] = useState('ALL');
  // Install-event filter (2026-08-24) — [] = All Events; otherwise event
  // ids (+ INSTALL_EVENT_UNSCHEDULED). Per-run TRANSIENT by decision:
  // never saved to the schedules row.
  const [installEventFilter, setInstallEventFilter] = useState([]);


  useEffect(function() {
    if (!exportDropOpen) return;
    function handleClick() { setExportDropOpen(false); }
    document.addEventListener('click', handleClick);
    return function() { document.removeEventListener('click', handleClick); };
  }, [exportDropOpen]);

  useEffect(()=>{
    const available = items.filter(it=>{
      if(!isParent(it)) return false;
      if(projectLabel && it.project!==projectLabel) return false;
      if(scheduleType==='ALL') return true;
      if(scheduleType==='FF&E') return it.schedule==='Furniture'||it.schedule==='Equipment';
      if(scheduleType==='Finish Schedule') return it.schedule==='Finish Schedule'||it.schedule==='Tile & Stone';
      return it.schedule===scheduleType;
    });
    // offScheduleDefault items still appear in the picker (`available`),
    // they just don't pre-select. Bookkeeper can tick them on per-schedule.
    // A flagged assembly PEER defaults every sibling off too — the schedule
    // row is the assembly, so one checked peer reads as assembly intent.
    const flaggedAsmIds = new Set(items.filter(it=>it.assemblyId&&it.offScheduleDefault).map(it=>it.assemblyId));
    setSelectedIds(available.filter(it=>!it.offScheduleDefault&&!(it.assemblyId&&flaggedAsmIds.has(it.assemblyId))).map(it=>it.id));
    setInstallExtraIds([]);
    setInstallExcludeIds([]);
    setInstallEventFilter([]); // event ids are per-project — a stale filter would blank the run
  },[projectLabel,scheduleType]);

  function handleToggle(id){setSelectedIds(prev=>prev.includes(id)?prev.filter(x=>x!==id):[...prev,id]);}
  function handleToggleAll(filteredItems){
    const allSel=filteredItems.every(it=>selectedIds.includes(it.id));
    if(allSel) setSelectedIds(prev=>prev.filter(id=>!filteredItems.find(it=>it.id===id)));
    else setSelectedIds(prev=>[...new Set([...prev,...filteredItems.map(it=>it.id)])]);
  }

  async function handleExportDocs(docType) {
    setExportDropOpen(false);
    if (selectedIds.length === 0) {
      alert('Select at least one item before exporting.');
      return;
    }
    setExporting(true);
    setExportResult(null);
    try {
      var JSZip = await new Promise(function(resolve, reject) {
        if (window.JSZip) { resolve(window.JSZip); return; }
        var script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/jszip/3.10.1/jszip.min.js';
        script.onload = function() { resolve(window.JSZip); };
        script.onerror = function() { reject(new Error('Could not load JSZip')); };
        document.head.appendChild(script);
      });
      var zip = new JSZip();

      var docsResult = await supabase
        .from('item_documents')
        .select('*')
        .in('item_id', selectedIds);

      if (docsResult.error)
        throw new Error('Could not load documents: ' + docsResult.error.message);

      var matchingDocs = groupDocsByType(docsResult.data || [], docType);

      var itemsMissingDoc = selectedIds.filter(function(id) {
        return !matchingDocs.some(function(d) { return d.item_id === id; });
      });

      if (matchingDocs.length === 0) {
        setExportResult({
          success: false,
          message: 'No ' + docType + ' documents found for the selected items.',
          missing: selectedIds.length,
        });
        setExporting(false);
        return;
      }

      var added = 0;
      for (var i = 0; i < matchingDocs.length; i++) {
        var doc = matchingDocs[i];
        var storagePath = doc.path || doc.file_path;
        if (!storagePath) continue;
        try {
          var signedResult = await supabase.storage
            .from('item-documents')
            .createSignedUrl(storagePath, 300);
          if (!signedResult.data || !signedResult.data.signedUrl) continue;
          var fileRes = await fetch(signedResult.data.signedUrl);
          if (!fileRes.ok) continue;
          var blob = await fileRes.blob();
          var docItem = (items || []).find(function(it) {
            return it.id === doc.item_id;
          });
          var rawExt = (doc.file_name || doc.name || 'pdf').split('.').pop() || 'pdf';
          var typeSlug = (docType === 'All'
            ? (doc.doc_type || doc.type || 'Doc')
            : docType
          ).replace(/\s+/g, '');
          var filename;
          if (docItem) {
            var safeTag = docItem.tag
              ? docItem.tag.replace(/[^a-zA-Z0-9\-\/]/g, '')
              : (docItem.roomNumber || 'NoTag');
            var productSlug = (docItem.productName || docItem.item || 'Item')
              .replace(/[^a-zA-Z0-9]/g, '').slice(0, 30);
            filename = safeTag + '_' + productSlug + '_' + typeSlug + '.' + rawExt;
          } else {
            filename = doc.file_name || doc.name || ('doc_' + i + '.' + rawExt);
          }
          zip.file(filename, blob);
          added++;
        } catch(e) {
          console.warn('Could not fetch doc:', doc.name, e);
        }
      }

      if (added === 0) {
        setExportResult({
          success: false,
          message: 'Could not retrieve files. Check storage permissions.',
          missing: selectedIds.length,
        });
        setExporting(false);
        return;
      }

      var zipBlob = await zip.generateAsync({ type: 'blob' });
      var zipName = buildExportZipName(projectLabel, scheduleType, docType);
      var a = document.createElement('a');
      a.href = URL.createObjectURL(zipBlob);
      a.download = zipName;
      a.click();
      URL.revokeObjectURL(a.href);

      setExportResult({
        success: true,
        added: added,
        missing: itemsMissingDoc.length,
        total: selectedIds.length,
        zipName: zipName,
        docType: docType,
      });
    } catch(e) {
      setExportResult({
        success: false,
        message: e.message || 'Export failed.',
      });
    }
    setExporting(false);
  }

  const selectedProject=(projects||[]).find(p=>p.label===projectLabel);

  // Which peer of an assembly stands for it in the install picker (2026-11-02):
  // the FINISHED piece — the peer shipping to a receiver, never the fabric
  // or frame on its way to a workroom. Mirrors computeInstallSelectionIds.
  // WT assemblies never select individually (they ride the synthetic
  // '__wt_asm__' picker row).
  const finishedPeerIdByAsm = useMemo(() => {
    const by = {};
    (items||[]).forEach(it => { if (it.assemblyId && isParent(it)) (by[it.assemblyId] = by[it.assemblyId] || []).push(it); });
    const out = {};
    Object.keys(by).forEach(a => {
      const f = finishedPeerForAssembly(by[a], { vendors: vendors || [], projectReceiverId: selectedProject && selectedProject.receiverId });
      if (f) out[a] = f.id;
    });
    return out;
  }, [items, vendors, selectedProject]);
  function selectsIndividually(it) {
    if (!it.assemblyId) return true;
    if (it.schedule === 'Window Treatments') return false;
    return finishedPeerIdByAsm[it.assemblyId] === it.id;
  }

  const installReceivers = useMemo(() => {
    const seen = new Set();
    (items||[]).forEach(it => {
      if (!isParent(it)) return;
      if (!selectsIndividually(it)) return;
      if (projectLabel && it.project !== projectLabel) return;
      const rid = it.shipToVendorId || selectedProject?.receiverId;
      if (rid) seen.add(String(rid));
    });
    return Array.from(seen)
      .map(rid => (vendors||[]).find(v => String(v.id) === rid))
      .filter(Boolean);
  }, [items, vendors, projectLabel, selectedProject, finishedPeerIdByAsm]);

  // Named install events for the event filter — soonest first, undated
  // last (sortInstallEvents without todayIso: pure chronological order).
  const installEvents = useMemo(
    () => sortInstallEvents((selectedProject && selectedProject.installEvents) || []),
    [selectedProject]);
  const projectLabels=activeProjects(projects, projectLabel).map(p=>p.label);

  // Build rooms data from selected items
  function buildRooms() {
    const selectedItems = items.filter(it=>selectedIds.includes(it.id));
    const COMP_ORDER = {'':0,'Fabric':1,'Trim':2,'Workroom':3,'Hardware':4,'Roller Shades':5};

    function priceSummary(netPerUnit, qty, markupPct) {
      const np=parseFloat(String(netPerUnit||'').replace(/[$,]/g,''))||0;
      if(!np) return '';
      const q=parseFloat(qty||'1')||1;
      const m=parseFloat(markupPct||30)||30;
      const fmt=n=>'$'+Math.round(n).toLocaleString('en-US');
      return fmt(np)+'/ea\n'+fmt(np*q)+' net\n'+fmt(np*q*(1+m/100))+' client';
    }

    function mapItem(it) {
      const heroImg=it.imageUrl||(it.images&&it.images.length>0?it.images[it.heroIndex||0]?.url:null);
      // Window treatment child component specs
      const children=it.children||[];
      const wkrm=children.find(c=>c.childType==='Workroom');
      const hw=children.find(c=>c.childType==='Hardware');
      const shade=children.find(c=>c.childType==='Roller Shades');
      const ws=wkrm?.specs||{};
      const hs=hw?.specs||{};
      const ss=shade?.specs||{};
      const hwParts=[hw?.item||'',hs.hwType||'',hs.finish||'',hs.diameter?hs.diameter+' dia':''].filter(Boolean);
      const isDrapery=!!wkrm;
      const motorized=isDrapery?ws.motorized:ss.motor;
      const motorBrand=isDrapery?(ws.motorBrand||hs.motorBrand||''):ss.motorBrand||'';
      const wtDescParts=isDrapery
        ?[((ws.pleat?ws.pleat+' ':'')+'Drapery'),ws.lining&&ws.lining!=='Unlined'?ws.lining+' Lined':''].filter(Boolean)
        :shade?['Roller Shade',ss.opacity||''].filter(Boolean):[];
      const wt_desc=wtDescParts.join(', ')||it.item||'';
      return {
        // _schedule — carry the source item's schedule type through to the
        // render layer so the Finish construction schedule can branch its
        // column mapping when a Tile & Stone item is mixed in (per
        // 2026-06-22 distribution pass).
        _schedule: it.schedule||'',
        item:it.item||'',product_name:it.productName||it.item||'',tag:it.tag||'',sidemark:it.sidemark||'',
        qty:it.qty||'1',qty_type:it.qtyType||'',vendor:it.vendor||'',
        manufacturer:it.manufacturer||it.specs?.manufacturer||'',
        model:it.model||it.specs?.model||'',
        size:it.dimensions||it.specs?.dimensions||it.specs?.size||'',
        finish:it.finish||it.specs?.finish||'',
        technology:it.specs?.technology||'',bulb:it.specs?.bulb||'',base:it.specs?.base||'',
        wattage:it.specs?.wattage||'',lumens:it.specs?.lumens||'',kelvins:it.specs?.kelvins||'',
        cri:it.specs?.cri||'',voltage:it.specs?.voltage||'',circuitry:it.specs?.circuitry||'',
        notes:it.notes||'',status:it.status||'',net_price:it.netPrice||'',
        // Order Status — 'Delivered' for Received/Installed/Complete,
        // 'ETA: <date>' for in-flight, em-dash otherwise. Workroom
        // schedule's right-most column reads this. (2026-06-29)
        order_status: formatOrderStatus(it),
        price_summary:priceSummary(it.netPrice,it.qty,it.markupPct),
        lead_time:it.leadTime||'',source_url:it.sourceUrl||'',image_url:heroImg||'',
        wt_mount:ws.mountType||'',
        wt_width:ws.finishedWidth||ss.finishedWidth||'',
        wt_length:ws.finishedLength||ss.finishedLength||'',
        wt_panels:ws.numPanels||'',
        wt_draw:ws.draw||'',
        wt_style:ws.pleat||ss.opacity||'',
        wt_lining:ws.lining||'',
        wt_hardware:hwParts.join(' · '),
        wt_motor:motorized==='Yes'?(motorBrand?'Motor: '+motorBrand:'Motorized'):'',
        wt_desc,
        mw_material:it.specs?.material||'',
        mw_species:it.specs?.speciesGrade||'',
        mw_profile:it.specs?.profileStyle||'',
        mw_paint:it.specs?.paintStainColor||'',
        mw_linear:it.specs?.linearFootage||'',
        mw_notes:[it.specs?.installNotes,it.specs?.shopDrawingRef,it.notes].filter(Boolean).join(' · '),
        gpm:it.gpm||it.specs?.gpm||'',
        spec_notes:it.specNotes||'',
        voltage_fuel:it.specs?.voltageFuel||'',
        btu:it.specs?.btu||'',
        install_notes:it.specs?.installNotes||'',
        ts_material:it.specs?.material||'',
        ts_tile_size:it.specs?.size||'',
        ts_thickness:it.specs?.thickness||'',
        ts_coverage_notes:it.specs?.coverageNotes||'',
        ts_grout_spec:it.specs?.groutSpec||'',
        wr_numbers:(it.wrEntries||[]).filter(e=>e&&e.wrNumber).map(e=>e.wrNumber).join(' / '),
      };
    }

    // Separate standalone items from assembly components
    const standalone = selectedItems.filter(it=>!it.assemblyId);
    const asmComponents = selectedItems.filter(it=>!!it.assemblyId);

    // Build one row per unique assembly, pulling spec data from the primary item
    const byAsmId = {};
    asmComponents.forEach(it=>{
      if(!byAsmId[it.assemblyId]) byAsmId[it.assemblyId]=[];
      byAsmId[it.assemblyId].push(it);
    });
    const asmRows = [];
    Object.entries(byAsmId).forEach(([asmId,asmItems])=>{
      const asm=(assemblies||[]).find(a=>a.id===asmId);
      if(!asm) return;
      // Primary item = the one without a childType (the sofa, fixture, etc.)
      // Fall back to the item with the most data if none found
      const primary = asmItems.find(it=>!it.childType) ||
                      asmItems.slice().sort((a,b)=>((b.manufacturer||b.vendor||'').length-(a.manufacturer||a.vendor||'').length))[0] ||
                      asmItems[0];
      const heroImg=primary.imageUrl||(primary.images&&primary.images.length>0?primary.images[primary.heroIndex||0]?.url:null);
      // Window Treatment assembly: extract specs from Workroom and Hardware member items
      const asmWkrm=asmItems.find(it=>it.childType==='Workroom');
      const asmHw=asmItems.find(it=>it.childType==='Hardware');
      const asmShade=asmItems.find(it=>it.childType==='Roller Shades');
      const aws=asmWkrm?.specs||{};
      const ahs=asmHw?.specs||{};
      const ass=asmShade?.specs||{};
      const asmHwParts=[asmHw?.item||'',ahs.hwType||'',ahs.finish||'',ahs.diameter?ahs.diameter+' dia':''].filter(Boolean);
      const asmIsDrapery=!!asmWkrm;
      const asmMotorized=asmIsDrapery?aws.motorized:ass.motor;
      const asmMotorBrand=asmIsDrapery?(aws.motorBrand||ahs.motorBrand||''):ass.motorBrand||'';
      const asmDescParts=asmIsDrapery
        ?[((aws.pleat?aws.pleat+' ':'')+'Drapery'),aws.lining&&aws.lining!=='Unlined'?aws.lining+' Lined':''].filter(Boolean)
        :asmShade?['Roller Shade',ass.opacity||''].filter(Boolean):[];
      const asm_wt_desc=asmDescParts.join(', ')||asm.name||'';
      // WT internal: expand each assembly component as its own row
      if(scheduleType==='Window Treatments'&&audience==='internal'){
        const sorted=[...asmItems].sort((a,b)=>(COMP_ORDER[a.childType||'']||0)-(COMP_ORDER[b.childType||'']||0));
        sorted.forEach((it,idx)=>{
          const itImg=it.imageUrl||(it.images&&it.images.length>0?it.images[it.heroIndex||0]?.url:null);
          const itnp=parseFloat(String(it.netPrice||'').replace(/[$,]/g,''))||0;
          const ct=it.childType||'';const s=it.specs||{};
          let specsStr='';
          const DRAW_MAP={'Left':'Left Drawn','Right':'Right Drawn','Center':'Center Split','Stationary':'Stationary','Stack Left':'Left Drawn','Stack Right':'Right Drawn','Center Split':'Center Split'};
          if(ct==='Workroom'){
            const dimStr=[s.finishedWidth,s.finishedLength].filter(Boolean).join('×');
            const drawLabel=DRAW_MAP[s.draw]||s.draw||'';
            const line1=[s.pleat,s.lining].filter(Boolean).join(' · ');
            const line2=[s.mountType,dimStr,s.numPanels?s.numPanels+' pnls':'',drawLabel].filter(Boolean).join(' · ');
            specsStr=[line1,line2].filter(Boolean).join('\n');
          } else if(ct==='Hardware'){
            const line1=[s.hwType,s.finish].filter(Boolean).join(' · ');
            const line2=[s.diameter?s.diameter+'" dia':'',s.motorBrand?'Motor: '+s.motorBrand:''].filter(Boolean).join(' · ');
            specsStr=[line1,line2].filter(Boolean).join('\n');
          } else if(ct==='Roller Shades'){specsStr=[s.opacity,s.motor==='Yes'?(s.motorBrand||'Motorized'):''].filter(Boolean).join(' · ');}
          else{
            const line1=[it.model||s.model,s.color||s.colorway].filter(Boolean).join(' · ');
            const line2=[s.content,s.fabricWidth?s.fabricWidth+'" wide':''].filter(Boolean).join(' · ');
            specsStr=[line1,line2].filter(Boolean).join('\n');
          }
          let sizeDim='';
          if(ct==='Workroom'||ct==='Roller Shades'){sizeDim=[s.finishedWidth,s.finishedLength].filter(Boolean).join('×');}
          else if(ct==='Hardware'){sizeDim=it.dimensions||s.diameter||'';}
          else{sizeDim=s.fabricWidth||it.dimensions||'';}  // Fabric / Trim
          asmRows.push({
            _roomName:asm.room_name||it.roomName||'No Room',_roomNumber:asm.room_number||it.roomNumber||'',
            _assemblyName:asm.name||'',_isFirstInAsm:idx===0,_compOrder:COMP_ORDER[ct]||0,
            comp_type:ct||'Fabric',image_url:itImg||'',
            item:it.productName||it.item||'',size_dim:sizeDim,vendor:it.vendor||'',specs:specsStr,
            qty:it.qty||'',unit:it.qtyType||'',
            net_price:itnp>0?'$'+itnp.toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2}):'',
            price_summary:priceSummary(it.netPrice,it.qty,it.markupPct),
            tag:asm.tag||it.tag||'',
          });
        });
        return;
      }
      // Standard (client/workroom/gc): collapse into one assembly row
      const asmParts = [];
      const fabricItem = asmItems.find(it=>!it.childType) || asmItems[0];
      if (fabricItem) asmParts.push({type:'Fabric', name:fabricItem.productName||fabricItem.item||'', vendor:fabricItem.vendor||'', net_price:fabricItem.netPrice||''});
      if (asmWkrm)   asmParts.push({type:'Workroom', name:asmWkrm.productName||asmWkrm.item||'', vendor:asmWkrm.vendor||'', net_price:asmWkrm.netPrice||''});
      if (asmHw)     asmParts.push({type:'Hardware', name:asmHw.productName||asmHw.item||'', vendor:asmHw.vendor||'', net_price:asmHw.netPrice||''});
      if (asmShade)  asmParts.push({type:'Roller Shade', name:asmShade.productName||asmShade.item||'', vendor:asmShade.vendor||'', net_price:asmShade.netPrice||''});
      // WT assemblies on non-internal schedules identify by the face fabric,
      // not the "Custom Drapery" wrapper. See pickWindowTreatmentScheduleDisplay.
      // The workroomOverrides sub-object is present only when audience is
      // 'workroom' and carries every workroom-only field (notes / quantity
      // display / source / panels block / hardware block) so the row push
      // reads from one obvious bag.
      const wtDisplay = pickWindowTreatmentScheduleDisplay(asmItems, scheduleType, audience);
      const wtOver = wtDisplay && wtDisplay.workroomOverrides;
      asmRows.push({
        tag:asm.tag||'',
        item: wtDisplay ? wtDisplay.productName : (asm.name||''),
        product_name: wtDisplay ? wtDisplay.productName : (primary.productName||asm.name||''),
        sidemark:asm.sidemark||'',
        qty: (wtOver && wtOver.quantityDisplay) ? wtOver.quantityDisplay : (primary.qty||'1'),
        qty_type:primary.qtyType||'',
        vendor:primary.vendor||'',
        // SOURCE column (workroom audience only): 2-line Mfr/Vendor block
        // from the Fabric peer. Empty string for other audiences — the cell
        // isn't rendered there.
        source: wtOver ? wtOver.source : '',
        manufacturer:primary.manufacturer||primary.specs?.manufacturer||'',
        model:primary.model||primary.specs?.model||'',
        size:primary.dimensions||primary.specs?.dimensions||primary.specs?.size||'',
        finish:primary.finish||primary.specs?.finish||'',
        technology:primary.specs?.technology||'', bulb:primary.specs?.bulb||'',
        base:primary.specs?.base||'', wattage:primary.specs?.wattage||'',
        lumens:primary.specs?.lumens||'', kelvins:primary.specs?.kelvins||'',
        cri:primary.specs?.cri||'', voltage:primary.specs?.voltage||'',
        circuitry:primary.specs?.circuitry||'',
        gpm:primary.gpm||primary.specs?.gpm||'',
        spec_notes:primary.specNotes||'',
        voltage_fuel:primary.specs?.voltageFuel||'',
        btu:primary.specs?.btu||'',
        install_notes:primary.specs?.installNotes||'',
        ts_material:primary.specs?.material||'',
        ts_tile_size:primary.specs?.size||'',
        ts_thickness:primary.specs?.thickness||'',
        ts_coverage_notes:primary.specs?.coverageNotes||'',
        ts_grout_spec:primary.specs?.groutSpec||'',
        notes: wtOver ? wtOver.notes : (asm.notes||primary.notes||''),
        status:primary.status||'',
        net_price:primary.netPrice||'', price_summary:priceSummary(primary.netPrice,primary.qty,primary.markupPct),
        lead_time:primary.leadTime||'',
        source_url:primary.sourceUrl||'',
        image_url: wtDisplay ? (wtDisplay.imageUrl||'') : (heroImg||''),
        wt_desc:asm_wt_desc,
        wt_mount:aws.mountType||'',
        wt_width:aws.finishedWidth||ass.finishedWidth||'',
        wt_length:aws.finishedLength||ass.finishedLength||'',
        wt_panels:aws.numPanels||'',
        wt_draw:aws.draw||'',
        wt_style:aws.pleat||ass.opacity||'',
        wt_lining:aws.lining||'',
        // Workroom audience overrides wt_hardware with the 3-line block
        // (name / Mfr / Vendor). Other audiences keep the legacy one-line
        // dot-joined string built from asmHwParts.
        wt_hardware: wtOver ? wtOver.hardware : asmHwParts.join(' · '),
        // PANELS column (workroom audience only): 3-line block consolidating
        // numPanels+draw, pleat+fullness, and mountType.
        wt_panels_block: wtOver ? wtOver.panels : '',
        // Order Status for the workroom schedule's right-most column
        // (2026-06-29). 'Delivered' for Received/Installed/Complete,
        // else 'ETA: <date>' from orderPlacedDate + leadWeeks, else
        // em-dash.
        // 2026-06-29 followup #2: orderPlacedDate gets stamped on
        // whichever peer landed on a vendor PO (could be Workroom,
        // Fabric, Hardware, or the parent — depends on which vendor
        // received the deposit first). Iterate every peer in the
        // assembly + the primary, pick the first one with an actual
        // orderPlacedDate. Status still comes from primary because
        // 'overall delivered' is an assembly-level state, not
        // per-peer.
        order_status: (function() {
          // Search EVERY peer for orderPlacedDate and leadTime
          // independently — the cascade can split them across peers
          // (Workroom peer carries leadWeeks; Fabric peer carries
          // orderPlacedDate; etc.). Take the first non-empty value
          // for each, regardless of which peer it lives on.
          var candidates = [primary].concat(asmItems.filter(function(it) { return it !== primary; }));
          var dateItem = candidates.find(function(it) { return it && it.orderPlacedDate; });
          var leadItem = candidates.find(function(it) { return it && (it.leadWeeks || (it.leadTime && String(it.leadTime).match(/\d/))); });
          return formatOrderStatus({
            status: primary.status,
            orderPlacedDate: dateItem ? dateItem.orderPlacedDate : '',
            leadWeeks:       leadItem ? leadItem.leadWeeks : '',
            leadTime:        leadItem ? leadItem.leadTime  : '',
          });
        })(),
        wt_motor:asmMotorized==='Yes'?(asmMotorBrand?'Motor: '+asmMotorBrand:'Motorized'):'',
        wr_numbers:(primary.wrEntries||[]).filter(e=>e&&e.wrNumber).map(e=>e.wrNumber).join(' / '),
        parts: asmParts,
        _roomName: asm.room_name||primary.roomName||'No Room',
        _roomNumber: asm.room_number||primary.roomNumber||'',
      });
    });

    const byRoom={};
    const roomNumMap={};
    // Key by roomNumber+roomName so identically-named rooms with different numbers stay separate
    function mkRoomKey(name,num){return num?(num+'\x1F'+name):name;}
    function roomKeyOf(it){return mkRoomKey(it.roomName||'No Room',it.roomNumber||'');}
    standalone.forEach(it=>{
      const room=roomKeyOf(it);
      if(!byRoom[room]) byRoom[room]=[];
      if(!roomNumMap[room]&&it.roomNumber) roomNumMap[room]=it.roomNumber;
      if(scheduleType==='Window Treatments'&&audience==='internal'&&it.children&&it.children.length>0){
        const asmName=it.productName||it.item||'';
        const heroImg=it.imageUrl||(it.images&&it.images.length>0?it.images[it.heroIndex||0]?.url:null);
        const np=parseFloat(String(it.netPrice||'').replace(/[$,]/g,''))||0;
        const fabricSpecs=[it.model,it.specs?.colorway,it.specs?.content].filter(Boolean).join(' · ');
        byRoom[room].push({_roomName:room,_roomNumber:it.roomNumber||'',_assemblyName:asmName,_isFirstInAsm:true,_compOrder:1,comp_type:'Fabric',image_url:heroImg||'',item:it.productName||it.item||'',size_dim:it.specs?.fabricWidth||it.dimensions||'',vendor:it.vendor||'',specs:fabricSpecs,qty:it.qty||'',unit:it.qtyType||'',net_price:np>0?'$'+np.toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2}):'',price_summary:priceSummary(it.netPrice,it.qty,it.markupPct),tag:it.tag||''});
        [...(it.children||[])].sort((a,b)=>(COMP_ORDER[a.childType||'']||99)-(COMP_ORDER[b.childType||'']||99)).forEach(child=>{
          const cnp=parseFloat(String(child.netPrice||'').replace(/[$,]/g,''))||0;
          const cs=child.specs||{};const cct=child.childType||'';
          let cSpecs='';
          const CDRAW_MAP={'Left':'Left Drawn','Right':'Right Drawn','Center':'Center Split','Stationary':'Stationary','Stack Left':'Left Drawn','Stack Right':'Right Drawn','Center Split':'Center Split'};
          if(cct==='Workroom'){
            const cdim=[cs.finishedWidth,cs.finishedLength].filter(Boolean).join('×');
            const cdraw=CDRAW_MAP[cs.draw]||cs.draw||'';
            const cl1=[cs.pleat,cs.lining].filter(Boolean).join(' · ');
            const cl2=[cs.mountType,cdim,cs.numPanels?cs.numPanels+' pnls':'',cdraw].filter(Boolean).join(' · ');
            cSpecs=[cl1,cl2].filter(Boolean).join('\n');
          } else if(cct==='Hardware'){
            const chl1=[cs.hwType,cs.finish].filter(Boolean).join(' · ');
            const chl2=[cs.diameter?cs.diameter+'" dia':'',cs.motorBrand?'Motor: '+cs.motorBrand:''].filter(Boolean).join(' · ');
            cSpecs=[chl1,chl2].filter(Boolean).join('\n');
          } else if(cct==='Roller Shades'){cSpecs=[cs.opacity,cs.motor==='Yes'?(cs.motorBrand||'Motorized'):''].filter(Boolean).join(' · ');}
          let cSizeDim='';
          if(cct==='Workroom'||cct==='Roller Shades'){cSizeDim=[cs.finishedWidth,cs.finishedLength].filter(Boolean).join('×');}
          else if(cct==='Hardware'){cSizeDim=child.dimensions||cs.diameter||'';}
          else{cSizeDim=cs.fabricWidth||child.dimensions||'';}
          byRoom[room].push({_roomName:room,_roomNumber:it.roomNumber||'',_assemblyName:asmName,_isFirstInAsm:false,_compOrder:COMP_ORDER[cct]||99,comp_type:cct,image_url:'',item:child.productName||child.item||'',size_dim:cSizeDim,vendor:child.vendor||'',specs:cSpecs,qty:child.qty||'',unit:child.qtyType||'',net_price:cnp>0?'$'+cnp.toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2}):'',price_summary:priceSummary(child.netPrice,child.qty,it.markupPct),tag:it.tag||''});
        });
      }else{
        const row = mapItem(it);
        // Workroom audience on WT schedules: format standalone trim/fabric
        // rows the same way as assembly rows — "<qty> <UNIT>" (e.g. "8 YD")
        // sourced from the standalone item's own qty + qtyType. Falls back
        // to the existing row.qty when either field is blank.
        if (scheduleType === 'Window Treatments' && audience === 'workroom') {
          const qDisp = formatFabricQuantity(it.qty, it.qtyType);
          if (qDisp) row.qty = qDisp;
        }
        byRoom[room].push(row);
      }
    });
    asmRows.forEach(row=>{
      const room=mkRoomKey(row._roomName||'No Room',row._roomNumber||'');
      if(!byRoom[room]) byRoom[room]=[];
      byRoom[room].push(row);
      if(!roomNumMap[room]&&row._roomNumber) roomNumMap[room]=row._roomNumber;
    });

    // Sort each room's items by tag; for WT internal, secondary sort by component order
    Object.values(byRoom).forEach(roomItems=>{
      if(scheduleType==='Window Treatments'&&audience==='internal'){
        roomItems.sort((a,b)=>{
          const tc=(a.tag||'').localeCompare(b.tag||'',undefined,{numeric:true,sensitivity:'base'});
          return tc!==0?tc:(a._compOrder||0)-(b._compOrder||0);
        });
      }else{
        roomItems.sort((a,b)=>(a.tag||'').localeCompare(b.tag||'',undefined,{numeric:true,sensitivity:'base'}));
      }
    });

    // Sort rooms by room number, then name
    return Object.entries(byRoom)
      .sort(([a],[b])=>{ const na=parseFloat(roomNumMap[a])||9999,nb=parseFloat(roomNumMap[b])||9999; return na!==nb?na-nb:a.localeCompare(b); })
      .map(([key,roomItems])=>{
        const sep=key.indexOf('\x1F');
        const roomName=sep>-1?key.slice(sep+1):key;
        return {room:roomName, roomNumber:roomNumMap[key]||'', items:roomItems};
      });
  }

  function buildInstallRooms() {
    const INSTALL_STATUSES = ['Ordered','Shipped','At Receiver','Delivered','Installed','Complete'];
    const isInstallEligible = (it) =>
      INSTALL_STATUSES.includes(it.status)
      || it.procurementClass === 'owner_furnished'
      || it.procurementClass === 'by_others';
    const installItems = items.filter(it => {
      // WT peers are handled separately below — they get collapsed into
      // one synthetic row per assembly. Skip them in the standard pass so
      // we don't emit a row per peer.
      if (it.schedule === 'Window Treatments' && it.assemblyId && it.childType) return false;
      if(!isParent(it)) return false;
      if(!selectsIndividually(it)) return false;
      if(projectLabel && it.project !== projectLabel) return false;
      if(scheduleType !== 'ALL') {
        if(scheduleType === 'FF&E') { if(it.schedule !== 'Furniture' && it.schedule !== 'Equipment') return false; }
        else if(scheduleType === 'Finish Schedule') { if(it.schedule !== 'Finish Schedule' && it.schedule !== 'Tile & Stone') return false; }
        else { if(it.schedule !== scheduleType) return false; }
      }
      if (receiverFilter !== 'ALL') {
        const effectiveReceiver = String(it.shipToVendorId || selectedProject?.receiverId || '');
        if (effectiveReceiver !== String(receiverFilter)) return false;
      }
      // Final inclusion rule (per-run override via the picker UI):
      //   (auto-eligible AND NOT excluded by user) OR (added by user)
      // Excludes win over auto-eligible; extras add non-eligible items.
      const isExcluded = installExcludeIds.includes(it.id);
      const isExtra    = installExtraIds.includes(it.id);
      if (isExcluded) return false;
      return isInstallEligible(it) || isExtra;
    });

    // Append one synthetic row per WT assembly. Picker can exclude
    // assemblies via the '__wt_asm__:' + asmId key. ScheduleType gate
    // still applies — WT assemblies only surface when the schedule
    // type is ALL or Window Treatments (intentional to avoid bleeding
    // WT into FF&E / Furniture / Lighting schedules firm-wide).
    if (scheduleType === 'ALL' || scheduleType === 'Window Treatments') {
      const wtRows = buildWindowTreatmentInstallRows(items, {
        assemblies, projectLabel, installExtraIds,
      });
      wtRows.forEach(row => {
        if (installExcludeIds.includes(row.id)) return;
        if (receiverFilter !== 'ALL') {
          const effectiveReceiver = String(row.shipToVendorId || selectedProject?.receiverId || '');
          if (effectiveReceiver !== String(receiverFilter)) return;
        }
        installItems.push(row);
      });
    }

    function mapInstallItem(it) {
      const heroImg = it.imageUrl||(it.images&&it.images.length>0?it.images[it.heroIndex||0]?.url:null);
      const sidemark = computeSidemark(projectLabel, it.roomName, it.roomNumber);
      const estShipIso = estimatedShipDate(it);
      let estShipPretty = '';
      if (estShipIso) {
        try {
          const d = new Date(estShipIso + 'T00:00:00');
          if (!isNaN(d.getTime())) {
            estShipPretty = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          }
        } catch (e) { /* leave blank */ }
      }
      return {
        tag:          it.tag||'',
        item:         it.item||'',
        product_name: it.productName||[it.manufacturer||it.specs?.manufacturer,it.model||it.specs?.model].filter(Boolean).join(' \xb7 ')||'',
        dimensions:   it.dimensions||it.specs?.dimensions||it.specs?.size||'',
        qty:          (it.qty||'1')+(it.qtyType?' '+it.qtyType:''),
        wrEntries:    (it.wrEntries||[]).filter(e=>e&&(e.wrNumber||e.description)),
        hero_url:     heroImg||'',
        vendor:       it.vendor||'',
        est_ship:     estShipPretty,
        sidemark,
        _roomName:    it.roomName||'No Room',
        _roomNumber:  it.roomNumber||'',
      };
    }

    const byRoom={}, roomNumMap={};
    installItems.forEach(it=>{
      const rk=(it.roomNumber||'')+'\x1F'+(it.roomName||'No Room');
      if(!byRoom[rk]) byRoom[rk]=[];
      byRoom[rk].push(mapInstallItem(it));
      if(!roomNumMap[rk]&&it.roomNumber) roomNumMap[rk]=it.roomNumber;
    });

    Object.values(byRoom).forEach(roomItems=>
      roomItems.sort((a,b)=>(a.tag||'').localeCompare(b.tag||'',undefined,{numeric:true,sensitivity:'base'}))
    );

    return Object.entries(byRoom)
      .sort(([a],[b])=>{const na=parseFloat(roomNumMap[a])||9999,nb=parseFloat(roomNumMap[b])||9999;return na!==nb?na-nb:a.localeCompare(b);})
      .map(([key,roomItems])=>{
        const sep=key.indexOf('\x1F');
        const roomName=sep>-1?key.slice(sep+1):key;
        return {room:roomName,roomNumber:roomNumMap[key]||'',items:roomItems};
      });
  }

  // the silently-broken document_versions save) deleted 2026-07-18 —
  // Cimon's Phase 5 sign-off. The new engine's Generate/Publish are the
  // only schedule outputs. Remaining legacy renderer purge tracked in
  // FOLLOWUPS 'Schedules Phase 5'.


  const INSTALL_STATUSES_UI = ['Ordered','Shipped','At Receiver','Delivered','Installed','Complete'];
  const isInstallEligibleUI = (it) =>
    !it.offScheduleDefault && (
      INSTALL_STATUSES_UI.includes(it.status)
      || it.procurementClass === 'owner_furnished'
      || it.procurementClass === 'by_others');
  function matchesScheduleType(it) {
    if(scheduleType==='ALL') return true;
    if(scheduleType==='FF&E') return it.schedule==='Furniture'||it.schedule==='Equipment';
    if(scheduleType==='Finish Schedule') return it.schedule==='Finish Schedule'||it.schedule==='Tile & Stone';
    return it.schedule===scheduleType;
  }
  const availableCount = items.filter(it=>{
    if(!isParent(it)) return false;
    if(projectLabel&&it.project!==projectLabel) return false;
    return matchesScheduleType(it);
  }).length;
  const installAutoCount = audience==='install'&&projectLabel ? (
    // Non-WT items: one count per eligible item.
    items.filter(it=>{
      // ANY assembly component — the selection engine skips them all
      // (WT peers ride their assembly row; other assemblies print via
      // their primary). Pre-fix only WT components were excluded, so
      // furniture/millwork/plumbing peers inflated this count with
      // rows the schedule never prints (the '90 items' audit).
      if(!selectsIndividually(it)) return false;
      if(!isParent(it)) return false;
      if(it.project!==projectLabel) return false;
      if(!matchesScheduleType(it)) return false;
      return isInstallEligibleUI(it);
    }).length
    // WT assemblies: one count per assembly with ≥1 eligible peer.
    + ((scheduleType==='ALL'||scheduleType==='Window Treatments')
        ? buildWindowTreatmentInstallRows(items, {assemblies, projectLabel, installExtraIds}).length
        : 0)
  ) : 0;
  const installExtraPool = audience==='install'&&projectLabel ? items.filter(it=>{
    if(!selectsIndividually(it)) return false; // components never select individually
    if(!isParent(it)) return false;
    if(it.project!==projectLabel) return false;
    if(!matchesScheduleType(it)) return false;
    return !isInstallEligibleUI(it);
  }) : [];

  // Unified install picker pool — all items in scope (project +
  // scheduleType), each tagged with auto-eligibility. WT assemblies
  // surfaced as their own synthetic rows so the bookkeeper can
  // include/exclude an assembly as one unit rather than per-peer.
  // Standard items first (sorted by room + product name), then WT
  // assemblies. Pre-checked = auto-eligible OR explicitly added;
  // unchecked = auto-eligible-but-excluded OR not auto-eligible.
  // 2026-06-09 — full picker replacing the auto-banner + extras-only UI.
  const installPickerRows = audience==='install'&&projectLabel ? (function() {
    const standard = items.filter(it=>{
      // ANY assembly component is excluded — the engine can't select
      // them individually (even as extras), so a picker row would be a
      // dead checkbox that inflates the count.
      if(!selectsIndividually(it)) return false;
      if(!isParent(it)) return false;
      if(it.project!==projectLabel) return false;
      if(!matchesScheduleType(it)) return false;
      return true;
    }).map(it=>({
      key: it.id,
      kind: 'item',
      item: it,
      autoEligible: isInstallEligibleUI(it),
    }));
    const wtAssemblies = (scheduleType==='ALL'||scheduleType==='Window Treatments')
      ? (function() {
          // Show every WT assembly in the project (regardless of peer
          // eligibility) so the bookkeeper can also INCLUDE assemblies
          // that don't yet have an eligible peer (rare but possible).
          // Auto-eligibility = current buildWindowTreatmentInstallRows
          // criterion (≥1 eligible peer).
          const eligibleSet = new Set(
            buildWindowTreatmentInstallRows(items, {assemblies, projectLabel, installExtraIds})
              .map(r => r.assemblyId)
          );
          const wtAsmIds = new Set(
            items.filter(it =>
              it.schedule === 'Window Treatments'
              && it.assemblyId
              && it.childType
              && it.project === projectLabel
            ).map(it => it.assemblyId)
          );
          return Array.from(wtAsmIds).map(asmId => {
            const asm = (assemblies||[]).find(a => a && a.id === asmId) || {};
            const firstPeer = items.find(it => it.assemblyId === asmId) || {};
            return {
              key: '__wt_asm__:' + asmId,
              kind: 'wt_assembly',
              item: {
                id: '__wt_asm__:' + asmId,
                productName: asm.name || firstPeer.productName || 'Window Treatment',
                roomName: asm.room_name || firstPeer.roomName || '',
                status: 'Window Treatment Assembly',
                imageUrl: firstPeer.imageUrl,
                images: firstPeer.images,
                heroIndex: firstPeer.heroIndex,
              },
              autoEligible: eligibleSet.has(asmId),
            };
          });
        })()
      : [];
    return [...standard, ...wtAssemblies].sort((a,b) => {
      const ra = (a.item.roomName||'').localeCompare(b.item.roomName||'');
      if (ra !== 0) return ra;
      return (a.item.productName||a.item.item||'').localeCompare(b.item.productName||b.item.item||'');
    });
  })() : [];

  function isInstallRowChecked(row) {
    if (row.autoEligible) return !installExcludeIds.includes(row.key);
    return installExtraIds.includes(row.key);
  }
  function toggleInstallRow(row) {
    if (row.autoEligible) {
      setInstallExcludeIds(prev =>
        prev.includes(row.key) ? prev.filter(x => x !== row.key) : [...prev, row.key]
      );
    } else {
      setInstallExtraIds(prev =>
        prev.includes(row.key) ? prev.filter(x => x !== row.key) : [...prev, row.key]
      );
    }
  }
  // The count must reflect what the schedule PRINTS — the selection
  // engine applies the receiver filter, so the count does too (Cimon
  // 2026-07-30: receiver dropdown set, few rows printed, count said 90).
  function installRowMatchesReceiver(row) {
    if (receiverFilter === 'ALL') return true;
    const projReceiver = selectedProject && selectedProject.receiverId;
    if (row.kind === 'wt_assembly') {
      const asmId = row.key.replace('__wt_asm__:', '');
      return items.some(it => it.assemblyId === asmId
        && String(it.shipToVendorId || projReceiver || '') === String(receiverFilter));
    }
    return String(row.item.shipToVendorId || projReceiver || '') === String(receiverFilter);
  }
  // Same rule for the event filter (2026-08-24): the selection engine
  // applies it, so the count does too. Assemblies match on any peer.
  function installRowMatchesEvent(row) {
    if (installEventFilter.length === 0) return true;
    const evs = (selectedProject && selectedProject.installEvents) || [];
    const matches = it => {
      const evId = itemInstallEventId(it, evs);
      return evId ? installEventFilter.includes(evId) : installEventFilter.includes(INSTALL_EVENT_UNSCHEDULED);
    };
    if (row.kind === 'wt_assembly') {
      const asmId = row.key.replace('__wt_asm__:', '');
      return items.some(it => it.assemblyId === asmId && matches(it));
    }
    return matches(row.item);
  }
  const installSelectedCount = installPickerRows.filter(r => isInstallRowChecked(r) && installRowMatchesReceiver(r) && installRowMatchesEvent(r)).length;

  const itemsSelectedCount = audience==='install' ? installSelectedCount : selectedIds.length;
  const itemsAvailableCount = audience==='install'
    ? installPickerRows.length
    : availableCount;

  // Batch select/clear for the Items view link-buttons.
  function handleSetAll(filteredItems, val){
    if (val) setSelectedIds(prev=>[...new Set([...prev, ...filteredItems.map(it=>it.id)])]);
    else setSelectedIds(prev=>prev.filter(id=>!filteredItems.some(it=>it.id===id)));
  }

  // Page layout per design_handoff_schedules_page (2026-07-07, high
  // fidelity): 1400px wrapper, ground-off context card with chevron
  // selects, ink-bordered ITEMS/SCHEDULE toggle (count = items AVAILABLE
  // for project+type; the beside-line carries the selected count).
  return h('div',{style:{maxWidth:1400,margin:'0 auto',padding:'36px 44px 80px'}},
    h('div',{style:{marginBottom:24}},
      h('div',{style:{fontSize:20,fontWeight:700,letterSpacing:'-0.01em',color:INK_1}},'Schedules'),
      h('div',{style:{fontSize:13,color:INK_3,marginTop:3}},'Select items and publish PDF schedules \u2014 one template, one audience, one dated issue at a time.')
    ),
    // Context controls card
    h('div',{style:{background:GROUND_OFF,border:'1px solid '+LINE_CONTAINER,borderRadius:8,padding:'16px 20px',display:'flex',alignItems:'flex-end',gap:28,flexWrap:'wrap'}},
      h(DSel,{label:'Project',value:projectLabel,onChange:setProjectLabel,minWidth:170},
        h('option',{value:''},'\u2014 Select project \u2014'),
        projectLabels.map(pl=>h('option',{key:pl,value:pl},pl))
      ),
      h(DSel,{label:'Schedule Type',value:scheduleType,onChange:setScheduleType,minWidth:190},
        getScheduleTypesForAudience(audience).map(sc=>h('option',{key:sc.key,value:sc.key},TYPE_DISPLAY[sc.key]||sc.key))
      ),
      h(DSel,{label:'Audience',value:audience,onChange:setAudience,minWidth:170},
        AUDIENCES.map(a=>h('option',{key:a.key,value:a.key},a.label))
      ),
      audience==='install' && installReceivers.length > 1 && h(DSel,{label:'Receiver',value:receiverFilter,onChange:setReceiverFilter,minWidth:170},
        h('option',{value:'ALL'},'All Receivers'),
        installReceivers.map(v=>h('option',{key:v.id,value:String(v.id)},v.name))
      ),
      audience==='install' && installEvents.length > 0 && h(InstallEventSelect,{
        events: installEvents, value: installEventFilter, onChange: setInstallEventFilter,
      }),
      h('div',{style:{display:'flex',flexDirection:'column',gap:6}},
        h('div',{style:EYEBROW_STYLE},'Date'),
        h('input',{value:date,onChange:e=>setDate(e.target.value),style:{background:'#fff',border:'1px solid '+LINE_CONTAINER,borderRadius:6,padding:'7px 12px',fontSize:13,color:INK_1,width:120,fontFamily:'inherit',boxSizing:'content-box'}})
      )
    ),
    // THE TOGGLE
    h('div',{style:{display:'flex',alignItems:'center',gap:20,margin:'28px 0 24px'}},
      h('div',{style:{display:'inline-flex',border:'1px solid #111111',borderRadius:6,overflow:'hidden'}},
        [['items','Items \u00b7 '+(projectLabel?itemsAvailableCount:'\u2014')],['schedule','Schedule']].map(function(seg,i){
          var k=seg[0], label=seg[1], active=view===k;
          return h('button',{
            key:k,
            onClick:function(){setView(k);},
            style:{
              minWidth:190,padding:'11px 24px',fontFamily:'inherit',fontSize:12,fontWeight:500,
              letterSpacing:'0.09em',textTransform:'uppercase',border:'none',cursor:'pointer',
              borderLeft:i===1?'1px solid #111111':'none',
              background:active?'#111111':'#FFFFFF',
              color:active?'#FFFFFF':INK_1,
            },
          },label);
        })
      ),
      projectLabel && h('div',{style:{fontSize:13,color:itemsSelectedCount>0?ZONE_GREEN:INK_2}},
        itemsSelectedCount>0
          ? '\u2713 '+itemsSelectedCount+' item'+(itemsSelectedCount!==1?'s':'')+' on this schedule'
          : '\u26a0 No items selected \u2014 pick them on the Items view'
      )
    ),
    // SCHEDULE view — the engine panel (grid, publish, versions, legacy row)
    view==='schedule' && h(NewEngineSchedulePanel,{
      projectLabel, scheduleType, audience, selectedIds,
      dateLabel: date, selectedProject, items, assemblies,
      receiverFilter, installExtraIds, installExcludeIds, installEventFilter,
      // Export item documents (kept + promoted 2026-07-18 — Cimon:
      // "we definitely need to keep export docs and make it more
      // visible"). This is NOT a schedule renderer: it zips the
      // selected items' ATTACHED documents (POs, spec sheets, quotes).
      // The legacy schedule Preview Draft / Save Version buttons that
      // used to share this row are gone — the new engine's Generate /
      // Publish are the only schedule outputs.
      legacySlot: h('div',{key:'exportdocs'},
        h('div',{style:{marginTop:32,paddingTop:16,borderTop:'1px solid '+LINE_CONTAINER},onClick:e=>e.stopPropagation()},
          h('div',{style:Object.assign({},EYEBROW_STYLE,{marginBottom:6})},'Export item documents'),
          h('div',{style:{fontSize:12,color:INK_3,marginBottom:10,maxWidth:520}},
            'Download the selected items\u2019 attached documents \u2014 purchase orders, spec sheets, quotes \u2014 as one zip.'),
          h('div',{style:{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}},
            h('div',{style:{position:'relative',display:'inline-block'}},
              h('button',{
                onClick:e=>{e.stopPropagation();setExportDropOpen(o=>!o);setExportResult(null);},
                disabled:exporting||selectedIds.length===0,
                style:Object.assign({
                  padding:'10px 20px',fontFamily:'inherit',fontSize:12,fontWeight:500,
                  letterSpacing:'0.06em',textTransform:'uppercase',cursor:'pointer',
                  border:'1px solid #111111',background:'#FFFFFF',color:INK_1,
                },(exporting||selectedIds.length===0)?{opacity:0.5,cursor:'default'}:{}),
              }, exporting?'Exporting\u2026':'Export docs \u25be'),
              exportDropOpen&&h('div',{
                style:{position:'absolute',left:0,top:'100%',marginTop:4,zIndex:50,background:'#fff',border:'1px solid '+LINE_CONTAINER,borderRadius:6,boxShadow:'0 8px 32px rgba(0,0,0,0.12)',minWidth:190,padding:4},
                onClick:e=>e.stopPropagation(),
              },
                EXPORTABLE_DOC_TYPES.map(type=>
                  h('button',{
                    key:type,
                    onClick:()=>handleExportDocs(type),
                    style:{display:'block',width:'100%',textAlign:'left',padding:'7px 10px',fontSize:11,color:INK_1,background:'none',border:'none',cursor:'pointer',borderRadius:4,fontFamily:'inherit'},
                    onMouseEnter:e=>{e.currentTarget.style.background=GROUND_OFF;},
                    onMouseLeave:e=>{e.currentTarget.style.background='none';},
                  },
                    type==='All' ? 'All documents'
                      : type==='Purchase Order' ? 'Purchase orders'
                      : type==='Spec Sheet' ? 'Spec sheets'
                      : type.toLowerCase()+'s'
                  )
                )
              )
            )
          ),
          exportResult&&h('div',{style:{marginTop:8,fontSize:11,color:exportResult.success?ZONE_GREEN:ALERT_RED_TEXT}},
            exportResult.success
              ? exportResult.added+' '+(exportResult.docType==='All'?'files':exportResult.docType.toLowerCase()+'s')+' downloaded \u2014 '+exportResult.zipName+(exportResult.missing>0?' \u00b7 '+exportResult.missing+' item'+(exportResult.missing!==1?'s':'')+' had no matching document':'')
              : (exportResult.message||'Export failed')
          )
        )
      ),
    }),
    view==='items' && h('div',null,
      audience==='install' ? h('div',null,
        // Unified install picker — pre-checked rows are auto-eligible
        // by status (Order Confirmed and later, plus OF/BO) and by WT
        // assembly peer eligibility. Bookkeeper can toggle any row.
        // Replaces the two-block "auto-included banner + extras pool"
        // UI (2026-06-09).
        projectLabel&&installPickerRows.length===0&&h('div',{style:{padding:'12px 16px',background:'#fff',border:'0.5px solid #C9A227',borderRadius:6,marginBottom:12,fontSize:12,color:ALERT_AMBER_TEXT}},
          '\u26a0 No items found for this project and schedule type.'
        ),
        projectLabel&&installPickerRows.length>0&&h('div',null,
          h('div',{style:{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}},
            h('div',{style:{fontSize:10,fontWeight:500,color:INK_3,letterSpacing:'0.07em',textTransform:'uppercase'}},
              'Install items — ',installSelectedCount,' of ',installPickerRows.length,' selected'
            ),
            h('div',{style:{fontSize:11,color:INK_3}},
              'Pre-checked = auto-eligible (Order Confirmed and later, plus Owner Furnished / By Others). Click any row to toggle.'
            )
          ),
          h('div',{style:{border:'0.5px solid '+LINE_CONTAINER,borderRadius:6,overflow:'hidden'}},
            h('table',{style:{width:'100%',borderCollapse:'collapse',fontSize:11}},
              h('tbody',null,
                installPickerRows.map((row,idx)=>{
                  const checked = isInstallRowChecked(row);
                  const it = row.item;
                  const heroImg = it.imageUrl||(it.images&&it.images.length>0?it.images[it.heroIndex||0]?.url:null);
                  const statusBadge = row.kind === 'wt_assembly'
                    ? 'WT Assembly · ' + (row.autoEligible ? 'peers eligible' : 'no eligible peers')
                    : (row.autoEligible ? it.status : it.status + ' · not yet eligible');
                  const statusInk = row.autoEligible ? INK_3 : ALERT_AMBER_TEXT;
                  return h('tr',{
                    key:row.key,
                    style:{
                      borderBottom:'0.5px solid '+LINE_ROW,
                      background:'#fff',
                      cursor:'pointer'
                    },
                    onClick:()=>toggleInstallRow(row)
                  },
                    h('td',{style:{padding:'4px 8px',width:32},onClick:e=>e.stopPropagation()},
                      h('input',{type:'checkbox',checked,onChange:()=>toggleInstallRow(row),style:{cursor:'pointer'}})
                    ),
                    h('td',{style:{padding:'4px 8px',width:44}},
                      heroImg
                        ? h('img',{src:heroImg,style:{width:36,height:36,objectFit:'cover',borderRadius:3}})
                        : h('div',{style:{width:36,height:36,background:GROUND_OFF,border:'0.5px solid '+LINE_ROW,borderRadius:3}})
                    ),
                    h('td',{style:{padding:'4px 8px'}},
                      h('div',{style:{fontWeight:600}},it.productName||it.item||'—'),
                      h('div',{style:{fontSize:10,color:statusInk}},
                        (it.roomName?it.roomName+' \xb7 ':'') + statusBadge
                      )
                    )
                  );
                })
              )
            )
          )
        )
      ) : h(ItemSelector,{items,scheduleType,projectLabel,selectedIds,onToggle:handleToggle,onToggleAll:handleToggleAll,onSetAll:handleSetAll})
    )
  );
}
